'use client';
import { motion } from 'framer-motion';
import { profile } from '@/data/profile';
import OrbitalObject from '../ui/OrbitalObject';

export default function Experience() {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const item = {
    hidden: { opacity: 0, x: -20 },
    show: { opacity: 1, x: 0 }
  };

  // Create 20 orbital objects with varying properties
  const orbitalConfigs = Array.from({ length: 20 }, (_, index) => ({
    color: [
      "#4f83ff", "#83ff4f", "#ff4f83", "#ff834f", "#834fff",
      "#4fff83", "#ff4fff", "#ffff4f", "#4fffff", "#ffffff",
      "#ff6b6b", "#4ecdc4", "#45b7d1", "#96ceb4", "#feca57",
      "#ff9ff3", "#54a0ff", "#5f27cd", "#00d2d3", "#ff9f43"
    ][index % 20],
    size: 4 + (index % 8) + 2, // Sizes from 6 to 13
    orbitDuration: 15 + (index % 25) + 5, // Duration from 20 to 44 seconds
    orbitRadius: 80 + (index % 15) * 20, // Radius from 80 to 360
    delay: (index % 10) * 2, // Delays from 0 to 18 seconds
  }));

  return (
    <section id="experience" className="py-20 bg-black relative overflow-hidden">
      {/* Map through 20 orbital objects */}
      {orbitalConfigs.map((config, index) => (
        <div key={index} className="absolute inset-0" style={{ left: '50%', top: '50%' }}>
          <OrbitalObject
            color={config.color}
            size={config.size}
            orbitDuration={config.orbitDuration}
            orbitRadius={config.orbitRadius}
            delay={config.delay}
          />
        </div>
      ))}

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <h2 className="text-3xl font-bold mb-12 text-center text-white">Experience</h2>

        <div className="max-w-3xl mx-auto">
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="space-y-8"
          >
            {profile.experience.map((exp, index) => (
              <motion.div
                key={index}
                variants={item}
                className="relative pl-8 border-l-2 border-blue-500/30 group"
              >
                <div className="absolute left-[-8px] top-0 w-4 h-4 rounded-full bg-blue-500 shadow-[0_0_10px_rgba(79,131,255,0.5)] group-hover:shadow-[0_0_15px_rgba(79,131,255,0.8)] transition-shadow" />
                <div className="absolute left-[-2px] top-0 h-full w-0.5 bg-gradient-to-b from-blue-500/50 to-transparent" />
                <div className="mb-1 text-sm text-blue-400 font-medium">{exp.period}</div>
                <h3 className="text-xl font-semibold text-white group-hover:text-blue-300 transition-colors">{exp.title}</h3>
                <div className="text-lg text-blue-400 mb-2">{exp.company}</div>
                <p className="text-gray-300">{exp.description}</p>
                <div className="absolute -left-1 top-4 h-full w-0.5 bg-blue-500/10 transform -skew-x-12" />
              </motion.div>
            ))}
          </motion.div>

          <div className="mt-16 relative">
            <h3 className="text-2xl font-bold mb-6 text-center text-white">Education</h3>
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="space-y-6"
            >
              {profile.education.map((edu, index) => (
                <motion.div
                  key={index}
                  variants={item}
                  className="text-center p-6 bg-zinc-800/50 backdrop-blur-sm rounded-lg border border-zinc-700/50 hover:border-blue-500/30 transition-colors group"
                  whileHover={{ scale: 1.02 }}
                >
                  <h4 className="text-lg font-semibold text-white group-hover:text-blue-300 transition-colors">{edu.degree}</h4>
                  <div className="text-blue-400">{edu.institution}</div>
                  <div className="text-gray-400">{edu.period}</div>
                  <div className="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg" />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}